import { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import AppLayout from "./components/app/AppLayout.jsx";
import ProtectedRoute from "./components/app/ProtectedRoute.jsx";
import OfficeRoute from "./components/app/OfficeRoute.jsx";
import { applyTheme } from "./components/app/ThemeSelector.jsx";
import LoginPage from "./pages/LoginPage.jsx";
import ClaimSearchPage from "./pages/ClaimSearchPage.jsx";
import NewClaimPage from "./pages/NewClaimPage.jsx";
import ClaimWorkspace from "./pages/ClaimWorkspace.jsx";
import SystemDataPage from "./pages/SystemDataPage.jsx";
import RdrPage from "./pages/RdrPage.jsx";
import LoggedAcceptedReportPage from "./pages/LoggedAcceptedReportPage.jsx";
import QccReportPage from "./pages/QccReportPage.jsx";
import CompletionsReportPage from "./pages/CompletionsReportPage.jsx";
import PredictiveInvoicingPage from "./pages/PredictiveInvoicingPage.jsx";
import FileLifeReportPage from "./pages/FileLifeReportPage.jsx";
import KpiReportPage from "./pages/KpiReportPage.jsx";
import MarketingCallsPage from "./pages/MarketingCallsPage.jsx";
import FeedbackReportPage from "./pages/FeedbackReportPage.jsx";
import ImportantJobsReportPage from "./pages/ImportantJobsReportPage.jsx";
import StrikeRateReportPage from "./pages/StrikeRateReportPage.jsx";
import JobScheduleReportPage from "./pages/JobScheduleReportPage.jsx";
import NonApprovedInvoicesReportPage from "./pages/NonApprovedInvoicesReportPage.jsx";
import OverrunsReportPage from "./pages/OverrunsReportPage.jsx";
import WorkGivenReportPage from "./pages/WorkGivenReportPage.jsx";
import PhonebookPage from "./pages/PhonebookPage.jsx";
import OfficeExpensesPage from "./pages/OfficeExpensesPage.jsx";
import FindInvoicePage from "./pages/FindInvoicePage.jsx";
import FindOrderPage from "./pages/FindOrderPage.jsx";
import OrderWorklistPage from "./pages/OrderWorklistPage.jsx";
import PerformanceReportPage from "./pages/PerformanceReportPage.jsx";
import InvoicingByMonthPage from "./pages/InvoicingByMonthPage.jsx";
import PhotoHelixLab from "./experiments/photo-helix/PhotoHelixLab.jsx"; // experiment, see its README
import { getCurrentUser } from "./services/apiClient.js";
import { setSessionUser } from "./services/pageDefaults.js";
// The inspector workspace mounts through ONE seam (src/inspection/index.jsx).
import { inspectionRoutes, isInspectionOnlyUser, INSPECTION_HOME } from "./inspection/index.jsx";

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Keep the page-defaults cache in lockstep with the signed-in user, updated
  // during render so it is populated BEFORE any page's initial-state function
  // runs (child renders precede parent effects — an effect here would be too
  // late for the first mount).
  setSessionUser(currentUser);

  useEffect(() => {
    getCurrentUser()
      .then((user) => {
        setCurrentUser(user);
        applyTheme(user.theme);
      })
      .catch(() => {
        setCurrentUser(null);
        applyTheme("zephyr");
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <LoginPage
            currentUser={currentUser}
            setCurrentUser={setCurrentUser}
          />
        }
      />
      <Route element={<ProtectedRoute currentUser={currentUser} loading={loading} />}>
        {inspectionRoutes({ currentUser, setCurrentUser })}
        <Route element={<OfficeRoute currentUser={currentUser} />}>
          <Route
            element={
              <AppLayout
                currentUser={currentUser}
                setCurrentUser={setCurrentUser}
              />
            }
          >
          {/* Login lands on the Dashboard (Robert 2026-07-13, "give Karen the win");
              flip index back to /claims/search if the team pushes back. */}
          <Route index element={<Navigate to="/rdr/main" replace />} />
          <Route path="/dashboard" element={<Navigate to="/rdr/main" replace />} />
          <Route path="/claims/search" element={<ClaimSearchPage />} />
          <Route path="/claims/new" element={<NewClaimPage />} />
          <Route path="/claims/:id" element={<ClaimWorkspace />} />
          <Route path="/admin/system-data" element={<SystemDataPage />} />
          <Route path="/rdr/workflow" element={<RdrPage section="workflow" />} />
          <Route path="/rdr/sla" element={<RdrPage section="sla" />} />
          <Route path="/rdr/main" element={<RdrPage section="main" />} />
          <Route path="/rdr/accounts" element={<RdrPage section="accounts" />} />
          <Route path="/rdr/health-safety" element={<RdrPage section="health-safety" />} />
          <Route path="/rdr/completions" element={<CompletionsReportPage />} />
          <Route path="/rdr/predictive" element={<PredictiveInvoicingPage />} />
          <Route path="/reports/logged-accepted" element={<LoggedAcceptedReportPage />} />
          <Route path="/reports/qcc" element={<QccReportPage />} />
          <Route path="/reports/file-life" element={<FileLifeReportPage />} />
          <Route path="/reports/kpi" element={<KpiReportPage />} />
          {/* EXPERIMENT, unlisted and in no menu — the tuning bench for the Zip
              All animation. This line plus its import are all of it here; delete
              both and src/experiments/photo-helix/ to remove the animation. See
              that folder's README. */}
          <Route path="/lab/photo-helix" element={<PhotoHelixLab />} />
          {/* Predefined Searches (1.0 PredefinedSearches.aspx) — all built
              2026-07-16, every one behind the reports master switch. */}
          <Route path="/reports/adverse-feedback" element={<FeedbackReportPage kind="adverse" />} />
          <Route path="/reports/positive-feedback" element={<FeedbackReportPage kind="positive" />} />
          <Route path="/reports/overruns" element={<OverrunsReportPage />} />
          <Route path="/reports/work-given" element={<WorkGivenReportPage />} />
          <Route path="/reports/job-schedule" element={<JobScheduleReportPage />} />
          <Route path="/reports/important-jobs" element={<ImportantJobsReportPage />} />
          <Route path="/reports/strike-rate" element={<StrikeRateReportPage />} />
          <Route path="/reports/non-approved-contractor-invoices" element={<NonApprovedInvoicesReportPage />} />
          <Route path="/reports/phonebook" element={<PhonebookPage />} />
          {/* Find section (Robert 2026-07-17): 1.0's InvoicesSearch/OrdersSearch. */}
          <Route path="/reports/find-invoice" element={<FindInvoicePage />} />
          <Route path="/reports/find-order" element={<FindOrderPage />} />
          {/* Remaining ports as visible stubs — green dot lands as each is built. */}
          <Route path="/reports/hs-order-worklist" element={<OrderWorklistPage report="hs" />} />
          <Route path="/reports/contractors" element={<OrderWorklistPage report="contractors" />} />
          <Route path="/reports/contractors-complete-needs-checking" element={<OrderWorklistPage report="needs_checking" />} />
          <Route path="/reports/performance" element={<PerformanceReportPage />} />
          <Route path="/reports/invoicing-by-month" element={<InvoicingByMonthPage />} />
          <Route path="/marketing" element={<MarketingCallsPage />} />
          {/* Office Expenses (2026-09-05): GPS's own bills with an approval
              trail — the register KR kept inside job 69773. Admin only. */}
          <Route path="/office-expenses" element={<OfficeExpensesPage />} />
          </Route>
        </Route>
      </Route>
      <Route
        path="*"
        element={
          <Navigate
            to={isInspectionOnlyUser(currentUser) ? INSPECTION_HOME : "/claims/search"}
            replace
          />
        }
      />
    </Routes>
  );
}

export default App;
